import { Search, Settings, Command, Gauge } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAppStore } from "@/store/useAppStore";

interface HeaderProps {
  onSearch: () => void;
  onAdmin: () => void;
}

/** Sticky frosted top bar — title, context, search, settings. */
export function Header({ onSearch, onAdmin }: HeaderProps) {
  const tableau = useAppStore((s) => s.tableau);
  const permission = useAppStore((s) => s.config.permission);
  // The Configuration entry point is only for workbook authors: show it in
  // Tableau's authoring mode (Desktop / web edit) and in standalone dev
  // preview — never for viewers on Server/Cloud.
  const isAuthoring = tableau?.mode === "authoring" || tableau?.mode === "dev";
  const canConfigure = isAuthoring && permission === "admin";

  return (
    <header className="sticky top-0 z-40 glass-sm mb-3 flex items-center justify-between gap-3 rounded-2xl px-3 py-1.5">
      <div className="flex items-center gap-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-[#5e5ce6] text-white shadow-glass-sm">
          <Gauge className="h-4 w-4" />
        </span>
        <div className="flex items-center gap-2">
          <h1 className="text-sm font-semibold tracking-tight">Smart Re Write</h1>
          {tableau && !tableau.available && (
            <Badge variant="warning">Dev preview</Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onSearch} className="gap-2">
          <Search className="h-4 w-4" />
          <span className="hidden sm:inline">Search</span>
          <kbd className="ml-1 hidden items-center gap-0.5 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground sm:inline-flex">
            <Command className="h-3 w-3" />K
          </kbd>
        </Button>
        {canConfigure && (
          <Button variant="ghost" size="icon" onClick={onAdmin} aria-label="Configuration">
            <Settings className="h-4 w-4" />
          </Button>
        )}
      </div>
    </header>
  );
}
